/**
 * Matrix store — the PWA-side canonical MIDI matrix.
 *
 * Lives in localStorage under MATRIX_KEY and syncs through OPBridge one
 * record at a time (`matrixSync.ts`, DEC-257). A new device STARTS EMPTY
 * (2026-10-07, operator: "start empty"): until then the first run fetched
 * `/matrix-seed.json`, the operator's own transcribed rig, so every new user
 * began with ortopilot's 18 devices. That file is now smoke test data
 * (`opbuttons-bot/fixtures/matrix-seed.json`); the operator's own devices
 * get their matrix from OPBridge.
 */

import { createSignal, type Accessor } from 'solid-js';
import {
  type MatrixDevice,
  type MatrixDeviceColor,
  type MatrixLibrary,
  type MatrixSlot,
  MATRIX_SCHEMA_VERSION,
  emptyMatrixLibrary,
  findSlot,
  slotKey,
} from '@opbuttons/schema';
import { ulid } from '../utils/ulid.ts';
import { createHistory } from '../undo/history.ts';
import { digest } from '../sync/recordDocSync.ts';

// Bumped to v2b on 2026-05-29 after the device-collapse importer rewrite,
// to invalidate any stored copies from the over-fragmented first import
// (which had 133 devices — every SL61 / LPX / Pacer control as its own
// "device"). Bumping the key force-clears that old data.
const MATRIX_KEY = 'opbuttons-matrix-v2b';
/** The seed as a device loaded it before 2026-10-07, so `matrixIsUneditedSeed`
 *  can still tell that copy from an edit. Nothing writes it any more. */
const SEED_MARK_KEY = 'opbuttons-matrix-seed-mark';

function loadFromStorage(): MatrixLibrary | null {
  try {
    const raw = localStorage.getItem(MATRIX_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as MatrixLibrary;
    if (parsed.schemaVersion !== MATRIX_SCHEMA_VERSION) {
      console.warn(`matrix: unknown schemaVersion ${parsed.schemaVersion}, ignoring stored copy`);
      return null;
    }
    return parsed;
  } catch (err) {
    console.warn('matrix: load failed', err);
    return null;
  }
}

function persist(lib: MatrixLibrary): void {
  try {
    localStorage.setItem(MATRIX_KEY, JSON.stringify(lib));
  } catch (err) {
    console.warn('matrix: persist failed', err);
  }
}

const [library, setLibrary] = createSignal<MatrixLibrary>(
  loadFromStorage() ?? emptyMatrixLibrary()
);

/** True while the matrix is EXACTLY the first-run seed, never edited here.
 *  Sync treats such a library as carrying no authority (S305): the seed is
 *  a snapshot of the rig from long ago, and pushing it would bring back
 *  devices the operator has deleted since. */
export function matrixIsUneditedSeed(): boolean {
  try {
    const mark = localStorage.getItem(SEED_MARK_KEY);
    return mark !== null && mark === digest(library());
  } catch { return false; }
}

export const matrixLibrary: Accessor<MatrixLibrary> = library;
export const matrixDevices: Accessor<MatrixDevice[]> = () => library().devices;
export const matrixSlots: Accessor<MatrixSlot[]> = () => library().slots;

/** Replace the entire matrix library. Persists + signals subscribers. */
export function setMatrixLibrary(next: MatrixLibrary): void {
  matrixHistory.checkpoint();
  setLibrary(next);
  persist(next);
}

/** History handle for the matrix library. Covers device CRUD, slot
 *  claim/unclaim, note-row reservation — everything through
 *  `setMatrixLibrary`. */
export const matrixHistory = createHistory<MatrixLibrary>({
  name: 'matrix',
  read: () => library(),
  write: (next) => {
    setLibrary(next);
    persist(next);
  },
});

/** Lookup helper — the slot occupying a (channel, ccNumber) cell, or
 *  undefined if free. */
export function slotForCc(channel: number, ccNumber: number): MatrixSlot | undefined {
  return findSlot(library(), { kind: 'cc', channel, ccNumber });
}

/** What the Matrix calls a CC square, for the layout linter (SD-14): the first
 *  RIG claim's device and control ("APC40 #1 Fader 1"), or null. A claim an
 *  OPButtons control owns (`itemId`, DEC-292) is skipped: on a duplicate CC it
 *  would only name one of the duplicates back. `channel0` is 0-based, like
 *  an item's channel; slots are 1..16. */
export function matrixCcLabel(channel0: number, cc: number): string | null {
  const slot = slotForCc(channel0 + 1, cc);
  const claim = slot?.claims.find((c) => !c.itemId);
  if (!claim) return null;
  const device = library().devices.find((d) => d.id === claim.deviceId);
  return device ? `${device.name} ${claim.controlLabel} in the Matrix` : `${claim.controlLabel} in the Matrix`;
}

/** Lookup helper — the slot occupying a (channel, noteLabel) cell. */
export function slotForNote(channel: number, noteLabel: string): MatrixSlot | undefined {
  return findSlot(library(), { kind: 'note', channel, noteLabel });
}

/** Lookup helper — the slot occupying a (channel, program) cell. */
export function slotForPc(channel: number, pcNumber: number): MatrixSlot | undefined {
  return findSlot(library(), { kind: 'pc', channel, pcNumber });
}

/** Visibility flag for the Matrix viewer modal. App.tsx renders the
 *  viewer when this is true; the header Matrix button toggles it. */
const [viewerOpen, setViewerOpen] = createSignal(false);
export const matrixViewerOpen: Accessor<boolean> = viewerOpen;
export function openMatrixViewer(): void { setViewerOpen(true); }
export function closeMatrixViewer(): void { setViewerOpen(false); }

// ─── Device CRUD (Phase 2) ─────────────────────────────────────────
// Each store mutation is a thin wrapper over a pure transform in
// matrix/devicesEdit.ts — keeping the math separately testable from
// the side-effecting localStorage/signals layer.

import {
  addDeviceTo,
  updateDeviceIn,
  removeDeviceFrom,
  moveDeviceIn,
  countClaimsForDevice,
  MATRIX_DEVICE_PALETTE as PALETTE,
} from './devicesEdit.ts';
import {
  addClaimAt as addClaimAtPure,
  updateClaim as updateClaimPure,
  removeClaim as removeClaimPure,
  removeSlot as removeSlotPure,
  reserveNoteRow as reserveNoteRowPure,
  reservePcRow as reservePcRowPure,
  type SlotLocator,
} from './slotsEdit.ts';
import type { MatrixClaim } from '@opbuttons/schema';
import { placeItemClaim, followItemClaim, dropItemClaims } from './itemClaims.ts';
import type { LayoutItem } from '../layout/projectStore.ts';

/** Add a new device. Name + color are optional — defaults are "New
 *  device" + next-available palette hue. Returns the created device so
 *  callers can immediately open it in the inline editor. */
export function addDevice(input: { name?: string; color?: MatrixDeviceColor; notes?: string } = {}): MatrixDevice {
  const { library: next, device } = addDeviceTo(library(), { id: ulid(), ...input });
  setMatrixLibrary(next);
  return device;
}

/** Patch a device by id. Unspecified fields stay as-is. */
export function updateDevice(
  id: string,
  patch: Partial<Pick<MatrixDevice, 'name' | 'color' | 'notes'>>,
): void {
  setMatrixLibrary(updateDeviceIn(library(), id, patch));
}

/** Count claims that reference this device — needed for the "12 slots
 *  will be orphaned" confirmation when deleting. */
export function claimsForDevice(id: string): number {
  return countClaimsForDevice(library(), id);
}

/** Remove a device. Also strips any claims pointing at it; slots that
 *  end up with zero claims after the strip are dropped entirely. */
export function removeDevice(id: string): void {
  setMatrixLibrary(removeDeviceFrom(library(), id));
}

/** Move a device's position in the devices[] array. Exported so callers
 *  can later wire a reorder affordance into the legend. */
export function moveDevice(id: string, toIndex: number): void {
  setMatrixLibrary(moveDeviceIn(library(), id, toIndex));
}

// ─── Slot + Claim CRUD (Phase 2) ──────────────────────────────────

/** Add a claim at a cell. Creates the slot if it doesn't exist yet.
 *  Returns the slot id the claim landed in. */
export function addClaim(
  loc: SlotLocator,
  input: { deviceId: string; controlLabel: string; notes?: string },
): string {
  const { library: next, slotId } = addClaimAtPure(library(), loc, {
    slotId: ulid(),
    claimId: ulid(),
    deviceId: input.deviceId,
    controlLabel: input.controlLabel,
    ...(input.notes !== undefined ? { notes: input.notes } : {}),
  });
  setMatrixLibrary(next);
  return slotId;
}

/** Patch a claim by id. */
export function updateClaim(
  claimId: string,
  patch: Partial<Pick<MatrixClaim, 'controlLabel' | 'notes'>>,
): void {
  setMatrixLibrary(updateClaimPure(library(), claimId, patch));
}

/** Remove a claim. Drops the slot if it was the last claim. */
export function removeClaim(claimId: string): void {
  setMatrixLibrary(removeClaimPure(library(), claimId));
}

// ─── Claims an item owns (2026-10-18, OPBridge S317) ───────────────
// Thin wrappers over `itemClaims.ts`; each writes only when the library
// actually changed, so an ordinary item edit costs no doc and no undo step.

const claimIds = () => ({ deviceId: ulid(), slotId: ulid(), claimId: ulid() });
function writeIfChanged(next: MatrixLibrary): void {
  if (next !== library()) setMatrixLibrary(next);
}

/** The control's Inspector picked a square: the control claims it. */
export function claimSquareForItem(item: LayoutItem): void {
  writeIfChanged(placeItemClaim(library(), item, claimIds()));
}

/** After an item edit: move / relabel / drop a claim it already owns. */
export function followItemClaimOf(item: LayoutItem): void {
  writeIfChanged(followItemClaim(library(), item, claimIds()));
}

/** Items were deleted: their claims go with them. */
export function dropClaimsOfItems(itemIds: readonly string[]): void {
  writeIfChanged(dropItemClaims(library(), itemIds));
}

/** Remove an entire slot (and all its claims). */
export function removeSlot(slotId: string): void {
  setMatrixLibrary(removeSlotPure(library(), slotId));
}

/** Reserve an empty note row so the user can click into it before
 *  adding a claim. Idempotent. */
export function reservePcRow(channel: number, pcNumber: number): void {
  setMatrixLibrary(reservePcRowPure(library(), ulid(), channel, pcNumber));
}

export function reserveNoteRow(channel: number, noteLabel: string): void {
  setMatrixLibrary(reserveNoteRowPure(library(), ulid(), channel, noteLabel));
}

export { slotKey, PALETTE as MATRIX_DEVICE_PALETTE };
export type { SlotLocator } from './slotsEdit.ts';
